/** Auto-resolve a UK rail employer name to a logo URL.
 *
 * As of Dec 2025, Clearbit's free Logo API was shut down. We use a
 * two-tier strategy:
 *
 *   1. Primary: Apistemic Logos (free, no key, high-quality WebP)
 *      → https://logos-api.apistemic.com/domain:{domain}
 *   2. Fallback: Google S2 favicon (always works, no attribution)
 *      → https://www.google.com/s2/favicons?domain={domain}&sz=128
 *   3. Final fallback (in component): coloured-initials avatar.
 *
 * Both endpoints are free and require no API key.
 */

const DOMAINS = {
  // Train operating companies
  "network rail": "networkrail.co.uk",
  "lner": "lner.co.uk",
  "london north eastern railway": "lner.co.uk",
  "avanti west coast": "avantiwestcoast.co.uk",
  "northern": "northernrailway.co.uk",
  "northern rail": "northernrailway.co.uk",
  "scotrail": "scotrail.co.uk",
  "south western railway": "southwesternrailway.com",
  "swr": "southwesternrailway.com",
  "transpennine express": "tpexpress.co.uk",
  "tpe": "tpexpress.co.uk",
  "west midlands trains": "westmidlandsrailway.co.uk",
  "west midlands railway": "westmidlandsrailway.co.uk",
  "great western railway": "gwr.com",
  "gwr": "gwr.com",
  "c2c": "c2c-online.co.uk",
  "greater anglia": "greateranglia.co.uk",
  "crosscountry": "crosscountrytrains.co.uk",
  "cross country": "crosscountrytrains.co.uk",
  "chiltern railways": "chilternrailways.co.uk",
  "govia thameslink railway": "thameslinkrailway.com",
  "govia thameslink": "thameslinkrailway.com",
  "thameslink": "thameslinkrailway.com",
  "gtr": "thameslinkrailway.com",
  "southern": "southernrailway.com",
  "gatwick express": "gatwickexpress.com",
  "great northern": "greatnorthernrail.com",
  "merseyrail": "merseyrail.org",
  "transport for wales": "tfw.wales",
  "tfw": "tfw.wales",
  "lumo": "lumo.co.uk",
  "hull trains": "hulltrains.co.uk",
  "grand central": "grandcentralrail.com",
  "caledonian sleeper": "sleeper.scot",
  "elizabeth line": "tfl.gov.uk",
  "tfl rail": "tfl.gov.uk",
  "transport for london": "tfl.gov.uk",
  "merseytravel": "merseytravel.gov.uk",
  // Infrastructure / promoters
  "hs2": "hs2.org.uk",
  "hs2 ltd": "hs2.org.uk",
  "high speed 2": "hs2.org.uk",
  "tfgm": "tfgm.com",
  // Major contractors / Tier-1
  "amey": "amey.co.uk",
  "colas rail": "colasrail.co.uk",
  "balfour beatty": "balfourbeatty.com",
  "volkerrail": "volkerrail.co.uk",
  "siemens mobility": "mobility.siemens.com",
  "hitachi rail": "hitachirail.com",
  "alstom": "alstom.com",
  "stadler": "stadlerrail.com",
  "rail operations group": "railopsgroup.co.uk",
  // Freight
  "freightliner": "freightliner.co.uk",
  "db cargo": "dbcargo.com",
  "gb railfreight": "gbrailfreight.com",
  "gbrf": "gbrailfreight.com",
  "direct rail services": "directrailservices.com",
};

/** Normalise a company name into a lookup key. */
function normalise(name) {
  return (name || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[.,'’"]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function getEmployerDomain(name) {
  if (!name) return null;
  const key = normalise(name);
  if (DOMAINS[key]) return DOMAINS[key];
  const slug = key.replace(/[^a-z0-9]/g, "");
  if (slug.length >= 3) return `${slug}.co.uk`;
  return null;
}

export function getEmployerLogo(name, override) {
  if (override && typeof override === "string" && override.startsWith("http")) return override;
  const domain = getEmployerDomain(name);
  if (!domain) return null;
  return `https://logos-api.apistemic.com/domain:${domain}`;
}

/** Fallback logo URL when the primary Apistemic lookup fails.
 * Google's S2 favicon endpoint is always reachable, requires no key,
 * and returns a usable square PNG for virtually every working domain. */
export function getEmployerLogoFallback(name) {
  const domain = getEmployerDomain(name);
  if (!domain) return null;
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
}

/** Two-letter initials from an employer name. */
export function getEmployerInitials(name) {
  if (!name) return "?";
  const parts = normalise(name).split(" ").filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
